import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { NoSSR } from '@ui/components/NoSSR';
import { GENERIC_DISPLAY_DECIMALS } from '@ui/constants/numbersDecimals';
import { Interval } from '@ui/constants/poll';
import { useTokenPriceLatest } from '@ui/hooks/price/usePriceLatest';
import { usePayTokenBalance } from '@ui/hooks/usePayTokenBalance';
import { Portal } from '@ui/lib/park/portal';
import { Field } from '@ui/lib/park/ui/field';
import { Label } from '@ui/lib/park/ui/styled/field';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { useContext } from 'react';
import { Box, Flex, HStack, VStack } from 'styled-system/jsx';

import { ExchangeContext } from './Context';
import { InputToken } from './InputToken';

type Props = {};

export const Output: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const {
    inputToken,
    outputToken,
    inputAmount,
    outputAmount,
    setInputToken,
    setOutputToken,
    setInputAmount,
    setOutputAmount,
    setAmountMode,

    outputTokens,
  } = useContext(ExchangeContext);

  const balance = usePayTokenBalance(outputToken, Interval.FAST);
  const price = useTokenPriceLatest(outputToken);

  return (
    <Field.Root
      background="#222222"
      gap="12px"
      // invalid={!!errorMessage}
      // {...p}
    >
      <Flex justifyContent="space-between">
        <Field.Context>
          {(context) => (
            <Field.Label
              textStyle="text.small"
              color={context.invalid ? 'primary.red' : 'greyWhite.lightGrey3'}
            >
              {t('common.Receive Amount')}
            </Field.Label>
          )}
        </Field.Context>
      </Flex>
      <Flex alignItems="center" gap="4px" justifyContent="space-between">
        <Box flex={1}>
          <Flex alignItems="center" justifyContent="flex-start">
            <Field.Input
              placeholder="0.0"
              textStyle="text.xlarge"
              fontWeight="bold"
              fontSize={20}
              disabled={true}
              w="full"
              caretColor="price.priceUp"
              value={outputAmount}
              onInput={(e) => {
                setAmountMode('exactOut');
                setOutputAmount(e.currentTarget.value);
              }}
              type="number"
              // ref={inputRefCallback}
              data-cy="ExchangeInput"
            />
          </Flex>
        </Box>
        <NoSSR>
          <InputToken
            setToken={setOutputToken}
            token={outputToken}
            payableTokens={outputTokens}
          />
        </NoSSR>
      </Flex>
      <Flex justifyContent="space-between">
        <Field.Label
          alignItems="center"
          display="flex"
          gap="4px"
          textStyle="text.small"
          color="greyWhite.lightGrey3"
        >
          <AmountUsd
            textStyle="text.xsmall"
            color="greyWhite.lightGrey4"
            value={
              price && outputAmount ? BigNumber(outputAmount).times(price) : 0
            }
            opts={{
              prefix: '~$',
              displayDecimals: GENERIC_DISPLAY_DECIMALS,
            }}
            data-cy="ExchangeInputEst-Usd"
            data-cy-value={0}
          />
        </Field.Label>
        <Field.Label
          alignItems="center"
          display="flex"
          gap="4px"
          textStyle="text.small"
          color="greyWhite.lightGrey3"
        >
          <Amount
            value={balance}
            _hover={{
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            opts={{
              prefix: 'Balance: ',
              saveDecimalsMode: true,
            }}
            onClick={() => {
              if (!balance) return;
              setAmountMode('exactOut');
              const decimals = outputToken?.decimals ?? 18;
              const maxAmount = balance
                .decimalPlaces(decimals, BigNumber.ROUND_DOWN)
                .toFixed();
              setOutputAmount(maxAmount);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              if (!balance) return;
              setAmountMode('exactOut');
              const decimals = outputToken?.decimals ?? 18;
              const maxAmount = balance
                .decimalPlaces(decimals, BigNumber.ROUND_DOWN)
                .toFixed();
              setOutputAmount(maxAmount);
            }}
            role="button"
            tabIndex={0}
            textDecoration="none"
            data-cy="TokenBalance"
          />
        </Field.Label>
      </Flex>
      {/* {leverageSetterEnabled && <LeverageSetter />}
                <Portal container={errPortalRef}>
                    <Error msg={errorMessage || ''} />
                </Portal> */}
    </Field.Root>
  );
};
